import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { formatDateOrEmpty, isUnsetDate } from '@helpers/dates';
import { styled } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import Button from '@components/Button';
import FieldWithActions from '@components/FieldWithActions';
import { ModalBase } from '@components/Modal';
import { Select } from '@components/Select';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { analyticsActions } from '@actions';
import { analyticsService } from '@services';
import { formatPercentNumber } from '@helpers/formatNumbers';
import CompetitorView from './CompetitorView';
import {
    EMPTY,
    formatReviews,
    showRescanError,
    StatTiles,
    Stat,
    SectionHeading,
    KeywordChips,
    GridMap,
    buildMarkers,
} from './visibilityShared';

const StyledModal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 640,
        '@media (max-width: 1024px)': {
            width: '100%',
        },
    },
    '& .modal-base-content': {
        padding: '16px 20px',
    },
    // Paired with the body class the Print report button sets: the modal becomes the whole
    // printed document — static full-width flow, no chrome, no toolbar or footer actions.
    '@media print': {
        'body.pl-print-report &': {
            position: 'static',
            left: 0,
            '& .pl-report-actions': {
                display: 'none',
            },
            '& .MuiBackdrop-root': {
                display: 'none',
            },
            '& .modal-base-paper': {
                position: 'static',
                transform: 'none',
                width: '100%',
                maxWidth: 'none',
                maxHeight: 'none',
                borderRadius: 0,
                boxShadow: 'none !important',
            },
            '& .modal-base-content': {
                height: 'auto',
                overflow: 'visible',
            },
            '& .modal-base-footer': {
                display: 'none',
            },
        },
    },
});

const CompetitorHeaderCell = styled(TableCell)({
    borderBottom: 'none',
    padding: '12px 16px',
    whiteSpace: 'nowrap',
    backgroundColor: 'var(--action-hover)',
});

const CompetitorBodyCell = styled(TableCell)({
    padding: '12px 16px',
});

const ActionsRow = styled(FieldWithActions)({
    marginBottom: 16,
});

const PRINT_REPORT_CLASS = 'pl-print-report';

const exitPrintReport = () => {
    document.body.classList.remove(PRINT_REPORT_CLASS);
    window.removeEventListener('afterprint', exitPrintReport);
};

const LocationModal = ({ open, onClose, detail, isLoading, error, onConfigure }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selectedKeywordId, setSelectedKeywordId] = useState(null);
    const [rescanRequested, setRescanRequested] = useState(false);
    const [competitorView, setCompetitorView] = useState(null);
    const competitorRequestToken = useRef(0);

    const closeCompetitorView = () => {
        competitorRequestToken.current += 1;
        setCompetitorView(null);
    };

    useEffect(() => {
        setSelectedKeywordId(prev =>
            detail?.keywords?.some(keyword => keyword.keywordId === prev)
                ? prev
                : (detail?.keywords?.[0]?.keywordId ?? null),
        );
        competitorRequestToken.current += 1;
        setCompetitorView(null);
    }, [detail]);

    useEffect(() => {
        setRescanRequested(false);
    }, [detail?.locationId]);

    // Print mode lives on <body>, outside this tree — unmounting mid-print would otherwise
    // leave the class behind and hide the app from every later print in the session.
    useEffect(() => () => exitPrintReport(), []);

    const selectedKeyword = detail?.keywords?.find(keyword => keyword.keywordId === selectedKeywordId);

    const locationMarkers = useMemo(() => buildMarkers(selectedKeyword), [selectedKeyword]);

    const scanOptions = useMemo(
        () =>
            (detail?.scans ?? [])
                .filter(scan => !isUnsetDate(scan.completedAt))
                .map(scan => ({
                    value: scan.id,
                    label: formatDateOrEmpty(scan.completedAt, 'dd MMM yyyy, HH:mm'),
                })),
        [detail?.scans],
    );
    const selectedScanId = scanOptions.some(option => option.value === detail?.scanId) ? detail.scanId : '';

    const hasCoordinates = detail?.centerLatitude != null && detail?.centerLongitude != null;
    const hasData = detail?.visibilityScore != null && detail?.keywords?.length > 0;
    const mapCenter = hasCoordinates ? { lat: +detail.centerLatitude, lng: +detail.centerLongitude } : null;

    const gridMeta = detail?.gridSize
        ? { size: detail.gridSize, km: detail.gridSpacingMeters ? detail.gridSpacingMeters / 1000 : null }
        : null;

    const onCompetitorClick = row => {
        if (row.isYou) return;
        const token = ++competitorRequestToken.current;
        setCompetitorView({ loading: true });
        analyticsService.getVisibilityCompetitor(detail.locationId, row.placeId, detail.scanId).then(
            data => {
                if (token !== competitorRequestToken.current) return;
                setCompetitorView({ data });
            },
            () => {
                if (token !== competitorRequestToken.current) return;
                setCompetitorView({ error: true });
            },
        );
    };

    const onRescan = () => {
        setRescanRequested(true);
        analyticsService.rescanVisibilityLocation(detail.locationId).then(
            () => {
                dispatch(analyticsActions.getVisibilityLocation(detail.locationId));
                setRescanRequested(false);
            },
            rescanError => {
                setRescanRequested(false);
                showRescanError(dispatch, t, rescanError);
            },
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return <Skeleton variant="rectangular" width="100%" height={400} />;
        }

        if (error || !detail) {
            return (
                <Typography variant="body1" align="center" sx={{ display: 'block', padding: '32px 0' }}>
                    {t('AnalyticsVisibility.loadError')}
                </Typography>
            );
        }

        if (!hasData) {
            return (
                <>
                    <Typography variant="body1" align="center" sx={{ display: 'block', padding: '32px 0 16px' }}>
                        {hasCoordinates ? t('AnalyticsVisibility.noDataYet') : t('AnalyticsVisibility.noCoordinates')}
                    </Typography>
                    {hasCoordinates && (
                        <div style={{ textAlign: 'center', paddingBottom: 32 }}>
                            <Button variant="outlined" onClick={onConfigure}>
                                {t('AnalyticsVisibility.configure')}
                            </Button>
                        </div>
                    )}
                </>
            );
        }

        if (competitorView) {
            return (
                <CompetitorView
                    view={competitorView}
                    center={mapCenter}
                    gridMeta={gridMeta}
                    onBack={closeCompetitorView}
                />
            );
        }

        return (
            <>
                <ActionsRow
                    className="pl-report-actions"
                    actions={[
                        {
                            icon: <SettingsRoundedIcon />,
                            label: t('AnalyticsVisibility.configure'),
                            tooltip: t('AnalyticsVisibility.configure'),
                            onClick: onConfigure,
                        },
                        {
                            icon: <LocalPrintshopIcon />,
                            label: t('AnalyticsVisibility.printReport'),
                            tooltip: t('AnalyticsVisibility.printReport'),
                            onClick: onPrintReport,
                        },
                    ]}
                >
                    {scanOptions.length > 1 ? (
                        <Select
                            label={t('AnalyticsVisibility.scanDate')}
                            options={scanOptions}
                            value={selectedScanId}
                            onChange={event =>
                                dispatch(analyticsActions.getVisibilityLocation(detail.locationId, event.target.value))
                            }
                        />
                    ) : (
                        <span />
                    )}
                </ActionsRow>
                <StatTiles>
                    <Stat
                        value={
                            detail.visibilityScore != null ? formatPercentNumber(detail.visibilityScore * 100) : EMPTY
                        }
                        label={t('AnalyticsVisibility.visibility')}
                    />
                    <Stat
                        value={detail.avgRank != null ? detail.avgRank.toFixed(1) : EMPTY}
                        label={t('AnalyticsVisibility.avgMapRank')}
                    />
                    <Stat
                        value={
                            selectedKeyword
                                ? `${selectedKeyword.top3Count}/${selectedKeyword.grid?.length || detail.totalPoints}`
                                : EMPTY
                        }
                        label={t('AnalyticsVisibility.top3Points')}
                    />
                </StatTiles>
                <SectionHeading>{t('AnalyticsVisibility.trackedKeywords')}</SectionHeading>
                <KeywordChips
                    keywords={detail.keywords}
                    selectedKeywordId={selectedKeywordId}
                    onSelect={setSelectedKeywordId}
                />
                <GridMap
                    keyword={selectedKeyword}
                    mapId="visibility-grid-map"
                    markers={locationMarkers}
                    center={mapCenter}
                    gridMeta={gridMeta}
                />
                {detail.competitors?.length > 0 && (
                    <>
                        <SectionHeading>{t('AnalyticsVisibility.whoWinsHere')}</SectionHeading>
                        <MuiTable size="small">
                            <TableHead>
                                <TableRow>
                                    <CompetitorHeaderCell>
                                        <Typography variant="subtitle2">{t('AnalyticsVisibility.business')}</Typography>
                                    </CompetitorHeaderCell>
                                    <CompetitorHeaderCell align="right">
                                        <Tooltip title={t('AnalyticsVisibility.inTop3Hint')} placement="top" arrow>
                                            <span>
                                                <Typography variant="subtitle2">
                                                    {t('AnalyticsVisibility.inTop3')}
                                                </Typography>
                                            </span>
                                        </Tooltip>
                                    </CompetitorHeaderCell>
                                    <CompetitorHeaderCell align="right">
                                        <Typography variant="subtitle2">{t('AnalyticsVisibility.rating')}</Typography>
                                    </CompetitorHeaderCell>
                                    <CompetitorHeaderCell align="right">
                                        <Tooltip title={t('AnalyticsVisibility.reviewsHint')} placement="top" arrow>
                                            <span>
                                                <Typography variant="subtitle2">
                                                    {t('AnalyticsVisibility.reviews')}
                                                </Typography>
                                            </span>
                                        </Tooltip>
                                    </CompetitorHeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {detail.competitors.map(competitor => (
                                    <TableRow
                                        key={competitor.placeId}
                                        hover={!competitor.isYou}
                                        onClick={() => onCompetitorClick(competitor)}
                                        sx={
                                            competitor.isYou
                                                ? { backgroundColor: 'var(--action-hover)' }
                                                : { cursor: 'pointer' }
                                        }
                                    >
                                        <CompetitorBodyCell>
                                            <Typography variant={competitor.isYou ? 'subtitle2' : 'body2'}>
                                                {competitor.title}
                                                {competitor.isYou ? ` (${t('AnalyticsVisibility.you')})` : ''}
                                            </Typography>
                                        </CompetitorBodyCell>
                                        <CompetitorBodyCell align="right">
                                            <Typography variant="body2">
                                                {competitor.topThreeShare != null
                                                    ? formatPercentNumber(competitor.topThreeShare * 100)
                                                    : EMPTY}
                                            </Typography>
                                        </CompetitorBodyCell>
                                        <CompetitorBodyCell align="right">
                                            <Typography variant="body2">
                                                {competitor.rating != null ? competitor.rating : EMPTY}
                                            </Typography>
                                        </CompetitorBodyCell>
                                        <CompetitorBodyCell align="right">
                                            <Typography variant="body2">
                                                {formatReviews(competitor.votes, competitor.velocityPerMonth)}
                                            </Typography>
                                        </CompetitorBodyCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </MuiTable>
                    </>
                )}
            </>
        );
    };

    const onPrintReport = () => {
        document.body.classList.add(PRINT_REPORT_CLASS);
        window.addEventListener('afterprint', exitPrintReport);
        setTimeout(() => {
            window.print();
        }, 100);
    };

    const showRescan = !isLoading && !!detail?.canRescan;

    return (
        <StyledModal
            isOpen={open}
            handleClose={onClose}
            title={detail?.name || ''}
            onPrimaryAction={showRescan ? onRescan : undefined}
            primaryActionText={
                detail?.scanInProgress ? t('AnalyticsVisibility.scanInProgress') : t('AnalyticsVisibility.runNewScan')
            }
            primaryActionType="outlined"
            primaryActionDisabled={!!detail?.scanInProgress || rescanRequested}
        >
            {renderContent()}
        </StyledModal>
    );
};

export default LocationModal;
